import { WEB_ERROR_CODES, type ApiErrorBody } from '@easyhelp/shared';

/**
 * Origine dell'API EasyHelp, senza slash finale. `VITE_API_URL` vuota o assente
 * significa **stessa origine**: in staging e produzione l'ALB instrada `/api/*` e
 * `/health` verso l'API (INFRA-003), così l'immagine web non dipende dall'ambiente.
 * In sviluppo il default è l'API locale.
 */
const configured = import.meta.env.VITE_API_URL?.trim();
export const API_URL = (configured || (import.meta.env.DEV ? 'http://localhost:4000' : '')).replace(/\/+$/, '');

/** Prefisso delle rotte versionate dell'API (tutte tranne `/health`). */
export const API_PREFIX = '/api/v1';

/**
 * Errore di una risposta HTTP non 2xx dell'API. Quando l'API ha risposto con un corpo
 * `ApiErrorBody`, `code` e `message` sono quelli dell'API e l'interfaccia può mostrarli.
 */
export class ApiError extends Error {
  readonly code: string | null;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    readonly status: number,
    readonly path: string,
    body: ApiErrorBody | null = null,
    cause?: unknown
  ) {
    super(body?.message ?? `L'API ha risposto ${status} su ${path}`, cause === undefined ? undefined : { cause });
    this.name = 'ApiError';
    this.code = body?.code ?? null;
    this.details = body?.details;
  }
}

/**
 * Chiamata all'API versionata con il cookie di sessione (`credentials: 'include'`, l'API è su
 * un'altra origine in sviluppo). Restituisce la risposta 2xx così com'è, per chi ne legge il corpo
 * come JSON o come file; lancia `ApiError` su risposta non 2xx, leggendo il corpo di errore se presente.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${API_URL}${API_PREFIX}${path}`, { credentials: 'include', ...init }).catch(
    (cause: unknown) => {
      // fetch fallisce solo per rete/CORS: il messaggio del browser è in inglese, qui uno leggibile.
      throw new ApiError(
        0,
        path,
        {
          statusCode: 0,
          code: WEB_ERROR_CODES.network,
          message: 'API non raggiungibile: controlla la connessione e riprova.',
        },
        cause
      );
    }
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, path, body && typeof body.code === 'string' ? body : null);
  }
  return response;
}

/** Chiamata JSON all'API versionata (`apiFetch`). Con 204 restituisce `undefined`. */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiFetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      // Con `FormData` (allegati, #43) il `Content-Type` multipart con il boundary lo mette il browser.
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
